import React, { useContext } from 'react';
import { Node } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import { Images } from 'utils';
import {
    INLINE_FIELD_ATTR,
    INLINE_FIELD_CLASS,
    INLINE_FIELD_ID_ATTR,
    INLINE_FIELD_VALUE_ATTR,
    inlineFieldTypes,
    inlineInputTypes,
    newInlineFieldId,
    writeInlineFieldValue,
} from '../../../utils/inlineFields';
import { InlineFillContext } from './inlineFillContext';
// Circular with inlineFieldsRichText.js (via inlineChoiceField.js); only used at render time.
import { InlineFieldControl } from './inlineFieldsRichText';

/**
 * TipTap node for an inline field inside RICH_TEXT (see `utils/inlineFields.js`
 * for the stored shape). In the template builder it is a chip — icon, label
 * and a red `*` when required — that selects the field for the Properties
 * panel on click.
 *
 * Filling a note (`fill` option, `RichTextElement fill`): the node draws its
 * fill control (`InlineFieldControl`) and writes the value into its own
 * attribute, so staff type and format the text around it in the same editor.
 * Staff cannot delete it there (`keepNodesPlugin`).
 */

export const inlineFieldIcon = (field) => {
    if (field?.type === inlineFieldTypes.SELECT) return Images.attributeDropDown;
    if (field?.type === inlineFieldTypes.CHECK_BOX) return Images.attributeCheckBox;
    if (field?.type === inlineFieldTypes.RADIO_BUTTON) return Images.attributeRadio;
    if (field?.defaultDateType === inlineInputTypes.DATE) return Images.attributeDate;
    return Images.attributeText;
};

/** In a note being filled: the field's control, its value kept on the node. */
const InlineFieldFill = ({ node, updateAttributes, fill }) => {
    const { field, fieldId, value } = node.attrs;
    const hidden = fill.hideStaffOnly && field?.properties?.staffOnly;
    return (
        <NodeViewWrapper as="span" className="inline-field-fill-node" contentEditable={false}>
            {!hidden && (
                <InlineFieldControl
                    field={field}
                    value={value}
                    disabled={fill.disabled}
                    error={fill.errorFor(fieldId)}
                    onBlur={() => fill.markTouched(fieldId)}
                    onChange={(next) => updateAttributes({ value: writeInlineFieldValue(field, next) })}
                />
            )}
        </NodeViewWrapper>
    );
};

const InlineFieldChip = (props) => {
    const { node, selected, extension } = props;
    const { field, fieldId } = node.attrs;
    const fill = useContext(InlineFillContext);
    if (fill) return <InlineFieldFill {...props} fill={fill} />;
    return (
        <NodeViewWrapper
            as="span"
            className={`inline-field-chip${selected ? ' inline-field-chip-selected' : ''}`}
            contentEditable={false}
            data-drag-handle=""
            onClick={() => extension.options.onSelect?.(fieldId)}
        >
            <img src={inlineFieldIcon(field)} alt="" className="inline-field-chip-icon" />
            {field?.label || 'Field'}
            {field?.properties?.required && <span className="inline-field-required">*</span>}
        </NodeViewWrapper>
    );
};

/**
 * Options for every chip's `ReactNodeViewRenderer`. TipTap caches a node
 * view's position and refreshes it only when that node itself changes, so
 * typing above a chip leaves it stale and a selection elsewhere outlines the
 * wrong chips; `trackNodeViewPosition` refreshes it on every update.
 * Filling: the control takes every event inside it, so ProseMirror never
 * selects or drags the node.
 */
export const chipNodeViewOptions = (fill) => ({ trackNodeViewPosition: true, ...(fill ? { stopEvent: () => true } : {}) });

const dedupeKey = new PluginKey('inlineFieldDedupe');

/**
 * While a note is filled, an edit may not remove one of the author's fields /
 * Goal blocks (backspace over it, select-and-type): a required field would
 * vanish with its validation. Content set by code (`setContent`, which marks
 * `preventUpdate`) still goes through.
 */
export const keepNodesPlugin = (key, typeName, idAttr) =>
    new Plugin({
        key: new PluginKey(key),
        filterTransaction: (tr, state) => {
            if (!tr.docChanged || tr.getMeta('preventUpdate')) return true;
            const ids = (doc) => {
                const found = new Set();
                doc.descendants((node) => {
                    if (node.type.name === typeName) found.add(node.attrs[idAttr]);
                });
                return found;
            };
            const after = ids(tr.doc);
            return [...ids(state.doc)].every((id) => after.has(id));
        },
    });

export const InlineField = Node.create({
    name: 'inlineField',
    group: 'inline',
    inline: true,
    atom: true,
    selectable: true,
    draggable: true,

    addOptions() {
        return {
            /** `(fieldId) => void` — a chip was clicked. */
            onSelect: null,
            /** A note being filled: fields are controls and cannot be deleted. */
            fill: false,
        };
    },

    addAttributes() {
        return {
            fieldId: { default: null, rendered: false },
            field: { default: null, rendered: false },
            value: { default: '', rendered: false },
        };
    },

    parseHTML() {
        return [
            {
                tag: `span[${INLINE_FIELD_ATTR}]`,
                getAttrs: (dom) => {
                    let field = null;
                    try {
                        field = JSON.parse(dom.getAttribute(INLINE_FIELD_ATTR));
                    } catch {
                        return false;
                    }
                    return {
                        fieldId: dom.getAttribute(INLINE_FIELD_ID_ATTR) || newInlineFieldId(),
                        field,
                        value: dom.getAttribute(INLINE_FIELD_VALUE_ATTR) || '',
                    };
                },
            },
        ];
    },

    // Same shape as `inlineFieldHtml` in utils/inlineFields.js.
    renderHTML({ node }) {
        const { field, fieldId, value } = node.attrs;
        return [
            'span',
            {
                class: INLINE_FIELD_CLASS,
                [INLINE_FIELD_ATTR]: JSON.stringify(field || {}),
                [INLINE_FIELD_ID_ATTR]: fieldId,
                ...(value ? { [INLINE_FIELD_VALUE_ATTR]: value } : {}),
            },
            field?.label || '',
        ];
    },

    renderText({ node }) {
        return node.attrs.field?.label || '';
    },

    addNodeView() {
        return ReactNodeViewRenderer(InlineFieldChip, chipNodeViewOptions(this.options.fill));
    },

    addCommands() {
        return {
            insertInlineField:
                ({ fieldId, field }) =>
                ({ commands }) =>
                    commands.insertContent([{ type: this.name, attrs: { fieldId, field, value: '' } }, { type: 'text', text: ' ' }]),
        };
    },

    /**
     * A field's id is how its value and its Properties panel find it, so two
     * copies (copy-paste inside the editor) must not share one: every copy
     * after the first gets a fresh id.
     */
    addProseMirrorPlugins() {
        return [
            ...(this.options.fill ? [keepNodesPlugin('inlineFieldKeep', this.name, 'fieldId')] : []),
            new Plugin({
                key: dedupeKey,
                appendTransaction: (transactions, _old, state) => {
                    if (!transactions.some((tr) => tr.docChanged)) return null;
                    const seen = new Set();
                    let tr = null;
                    state.doc.descendants((node, pos) => {
                        if (node.type.name !== this.name) return;
                        const id = node.attrs.fieldId;
                        if (id && !seen.has(id)) {
                            seen.add(id);
                            return;
                        }
                        tr = tr || state.tr;
                        tr.setNodeMarkup(pos, undefined, { ...node.attrs, fieldId: newInlineFieldId(), value: '' });
                    });
                    return tr;
                },
            }),
        ];
    },
});
